import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import type { PrintablePlayerSortName } from '@sorare/core/src/contexts/config';

import { commonCardsGridFragment } from '@sorare/marketplace/src/components/commonCard/CommonCardsGrid/types';
import { PaginatedCommonCardsGrid } from '@sorare/marketplace/src/components/commonCard/PaginatedCommonCardsGrid';
import { useDefaultPrintablePlayersSearchClient } from '@sorare/marketplace/src/hooks/search/useDefaultPrintablePlayersSearchClient';
import type { extractQueryGeneric } from '@sorare/marketplace/src/lib/searchClient';
import { commonCardsFilters } from '@sorare/marketplace/src/searchCards/mlb/cardFilters';

import { BaseballCardSearch } from 'components/searchCards/BaseballCardSearch';

import type {
  MLBPrintablePlayersSearchQuery,
  MLBPrintablePlayersSearchQueryVariables,
} from './__generated__/page.graphql';

const sorts: PrintablePlayerSortName[] = [
  'DESC-L15',
  'ASC-name_for_sorting',
  'DESC-name_for_sorting',
];

const PRINTABLE_PLAYERS_SEARCH_QUERY = gql`
  query MLBPrintablePlayersSearchQuery(
    $query: String!
    $page: Int
    $pageSize: Int
    $advancedFilters: String
    $sorts: [SearchSortInput!]!
    $refinements: [SearchRefinementInput!]!
    $facets: [SearchFacetDefinitionInput!]!
    $onlyFollowed: Boolean
  ) {
    searchPlayers(
      query: $query
      page: $page
      pageSize: $pageSize
      sorts: $sorts
      advancedFilters: $advancedFilters
      refinements: $refinements
      facets: $facets
      onlyFollowed: $onlyFollowed
    ) {
      ...useDefaultPrintablePlayersSearchClient_searchPrintablePlayers
      commonPlayerHits {
        id
        ...CommonCardsGrid_commonPlayer
      }
    }
  }
  ${useDefaultPrintablePlayersSearchClient.fragments.searchPrintablePlayers}
  ${commonCardsGridFragment}
` as TypedDocumentNode<
  MLBPrintablePlayersSearchQuery,
  MLBPrintablePlayersSearchQueryVariables
>;

const cardFilters = commonCardsFilters(false);

type Query = typeof PRINTABLE_PLAYERS_SEARCH_QUERY;

const AddCardPage = () => {
  const [onlyFollowed, setOnlyFollowed] = useState(false);
  const searchClient = useDefaultPrintablePlayersSearchClient<Query>(
    PRINTABLE_PLAYERS_SEARCH_QUERY,
    {
      onlyFollowed,
    },
    (data: extractQueryGeneric<Query>) => data.searchPlayers
  );

  return (
    <BaseballCardSearch
      sorts={sorts}
      analyticsTags={['Baseball', 'Swap']}
      cardFilters={cardFilters}
      attributesToRetrieve={['slug']}
      hideOwner
      GridComponent={PaginatedCommonCardsGrid}
      defaultHitsPerPage={40}
      hideSavedFilters
      alwaysShowFavoriteButton={false}
      selectionMode="drop"
      searchClient={searchClient}
      onFavoriteFilterChange={setOnlyFollowed}
    />
  );
};

export default AddCardPage;
